'use client';

import Pricing from "@/app/(landing)/components/pricing";
import Logo from "@/component/logo";
import { modal } from "@/component/modal";
import Slideshow from "@/component/slideshow";
import { useLanguage } from "@/context/language-context";
import { useTranslations } from "@/hooks/use-translations";
import { getLocalizedPath, routes } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { Button, Checkbox, CheckboxGroup, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Textarea } from "@heroui/react";
import { IconCheck, IconLink, IconUser } from "@tabler/icons-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface FormData {
    name: string;
    email: string;
    preferredDays: string[];
    hearAboutUs: string;
    message: string;
    termsPrivacy: boolean;
}

export default function RequestInfoClient() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { language } = useLanguage();
    const t = useTranslations();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        preferredDays: [],
        hearAboutUs: '',
        message: '',
        termsPrivacy: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);

    useEffect(() => {
        const errorParam = searchParams.get('error');
        if (errorParam) {
            setUrlError(errorParam);
        }
    }, [searchParams]);

    const updateFormData = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };


    const isFormValid = () => {
        return formData.name && formData.hearAboutUs && formData.termsPrivacy;
    };

    const handleSubmit = async () => {
        if (isFormValid()) {
            setIsSubmitting(true);
            setAuthError(null);
            try {
                // Send the registration data directly to Telegram
                const data = JSON.stringify({
                    source: 'vareselab.co',
                    language: language,
                    data: formData,
                }, null, 2);

                const response = await fetch('/api/telegram/send', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        text: data
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to send message, contact info@vareselab.co');
                }

                // Redirect to success page (localized)
                router.push(routes.contactReceived[language]);
            } catch (error: any) {
                console.error('Error sending request:', error);
                if (error.message) {
                    setAuthError(error.message);
                } else {
                    await modal.alert(
                        language === 'it' ? 'Errore' : 'Error',
                        {
                            message: language === 'it'
                                ? 'Si è verificato un errore durante l\'invio della richiesta. Contatta info@vareselab.co'
                                : 'An error occurred while sending the request. Please contact info@vareselab.co',
                            confirmText: 'OK'
                        }
                    );
                }
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    if (!t) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-default-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <style jsx>{`
                input, textarea, select {
                    font-size: 16px !important;
                }
                @media (max-width: 768px) {
                    input, textarea, select {
                        font-size: 16px !important;
                    }
                }
            `}</style>

            <Modal isOpen={authError !== null} onClose={() => setAuthError(null)}>
                <ModalContent>
                    <ModalHeader>Registration Error</ModalHeader>
                    <ModalBody>{authError ?? "Registration failed. Please try again."}</ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={() => setAuthError(null)}
                            aria-label="Close error message"
                        >
                            OK
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={urlError !== null} onClose={() => setUrlError(null)}>
                <ModalContent>
                    <ModalHeader>Authentication Error</ModalHeader>
                    <ModalBody>{urlError}</ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={() => setUrlError(null)}>
                            OK
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <div
                className={cn(
                    "bg-[radial-gradient(hsl(var(--heroui-primary-800)_/_0.15)_10%,hsl(var(--heroui-primary)_/_0.0)_67%)]",
                    "dark:bg-[radial-gradient(hsl(var(--heroui-primary-800)_/_0.1)_10%,hsl(var(--heroui-primary)_/_0.0)_67%)]",
                    "min-h-screen"
                )}
                style={{
                    backgroundPosition: "0 -150px",
                    backgroundSize: "auto 1600px",
                    backgroundRepeat: "no-repeat",
                }}
            >
                {/* Mobile slideshow at top */}
                <div className="desktop:hidden w-full h-64 sm:h-64">
                    <Slideshow />
                </div>

                <div className="min-h-screen w-full flex flex-col desktop:flex-row">
                    <div id="content" className="w-full desktop:w-3/5 flex flex-col overflow-y-auto p-4 desktop:p-6 scrollbar-hide relative z-10">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-0">
                            <div className="flex justify-between w-full gap-3">
                                <Logo />
                            </div>

                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <Button
                                    as={Link}
                                    href={getLocalizedPath('/', language)}
                                    target="_blank"
                                    className="text-xs text-default-600 flex-shrink-0 flex hover:underline sm:w-auto"
                                    radius="lg"
                                    variant="light"
                                    endContent={<IconLink size={16} />}
                                >
                                    {language === 'it' ? 'Scopri di più su VareseLab' : 'Know more about VareseLab'}
                                </Button>
                            </div>
                        </div>

                        <div className="mb-4 desktop:mb-4">
                            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{t.preRegistration.title}</h1>
                            <p className="text-sm sm:text-base text-default-600">{t.preRegistration.subtitle}</p>
                        </div>

                        <div className="flex-1 flex flex-col gap-6 desktop:gap-4">
                            {/* Pricing Display */}
                            <div className="mb-6 desktop:mb-8">
                                <Pricing showTitle={false} hideCallToAction={true} />
                            </div>

                            <div className="space-y-4 desktop:space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-3">
                                        {t.preRegistration.form.preferredDays.label}
                                    </label>
                                    <CheckboxGroup
                                        orientation="horizontal"
                                        value={formData.preferredDays}
                                        onValueChange={(value) => updateFormData('preferredDays', value)}
                                        classNames={{
                                            wrapper: "gap-2 flex-row flex-wrap"
                                        }}
                                    >
                                        <Checkbox value="monday">{t.preRegistration.form.preferredDays.options.monday}</Checkbox>
                                        <Checkbox value="tuesday">{t.preRegistration.form.preferredDays.options.tuesday}</Checkbox>
                                        <Checkbox value="wednesday">{t.preRegistration.form.preferredDays.options.wednesday}</Checkbox>
                                        <Checkbox value="thursday">{t.preRegistration.form.preferredDays.options.thursday}</Checkbox>
                                        <Checkbox value="friday">{t.preRegistration.form.preferredDays.options.friday}</Checkbox>
                                        <Checkbox value="saturday">{t.preRegistration.form.preferredDays.options.saturday}</Checkbox>
                                    </CheckboxGroup>
                                </div>

                                <Select
                                    label={t.preRegistration.form.hearAboutUs.label}
                                    placeholder={t.preRegistration.form.hearAboutUs.placeholder}
                                    selectedKeys={formData.hearAboutUs ? [formData.hearAboutUs] : []}
                                    onSelectionChange={(keys) => updateFormData('hearAboutUs', Array.from(keys)[0])}
                                    isRequired
                                    variant="bordered"
                                >
                                    <SelectItem key="instagram">{t.preRegistration.form.hearAboutUs.options.instagram}</SelectItem>
                                    <SelectItem key="facebook">{t.preRegistration.form.hearAboutUs.options.facebook}</SelectItem>
                                    <SelectItem key="linkedin">{t.preRegistration.form.hearAboutUs.options.linkedin}</SelectItem>
                                    <SelectItem key="google">{t.preRegistration.form.hearAboutUs.options.google}</SelectItem>
                                    <SelectItem key="friend">{t.preRegistration.form.hearAboutUs.options.friend}</SelectItem>
                                    <SelectItem key="other">{t.preRegistration.form.hearAboutUs.options.other}</SelectItem>
                                </Select>


                                <Textarea
                                    label={t.preRegistration.form.message.label}
                                    placeholder={t.preRegistration.form.message.placeholder}
                                    value={formData.message}
                                    onValueChange={(value) => updateFormData('message', value)}
                                    variant="bordered"
                                    minRows={3}
                                />

                                <Input
                                    label={t.preRegistration.form.name.label}
                                    placeholder={t.preRegistration.form.name.placeholder}
                                    type="text"
                                    autoComplete="name"
                                    startContent={<IconUser size={18} className="text-default-400" />}
                                    value={formData.name}
                                    onValueChange={(value) => updateFormData('name', value)}
                                    isRequired
                                    variant="bordered"
                                />

                                <Input
                                    label={t.preRegistration.form.email.label}
                                    placeholder={t.preRegistration.form.email.placeholder}
                                    type="email"
                                    autoComplete="email"
                                    startContent={<IconUser size={18} className="text-default-400" />}
                                    value={formData.email}
                                    onValueChange={(value) => updateFormData('email', value)}
                                    isRequired
                                    variant="bordered"
                                />

                                {/* Consent section */}
                                <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg">
                                    <div className="space-y-2 sm:space-y-3">
                                        <div className="flex items-start gap-2">
                                            <Checkbox
                                                isSelected={formData.termsPrivacy}
                                                onValueChange={(checked) => updateFormData('termsPrivacy', checked)}
                                                isRequired
                                            />
                                            <span className="text-sm">
                                                {language === 'it' ? 'Accetto l\'' : 'I agree to the '}
                                                <a
                                                    href="/docs/privacy-policy"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                >
                                                    {language === 'it' ? 'Informativa sulla Privacy' : 'Privacy Policy'}
                                                </a>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4 justify-center sm:justify-end desktop:mt-4 mb-8">
                                <Button
                                    color="primary"
                                    size="lg"
                                    className="w-full sm:w-auto"
                                    endContent={!isSubmitting ? <IconCheck size={18} /> : undefined}
                                    onPress={handleSubmit}
                                    isDisabled={!isFormValid() || isSubmitting}
                                    isLoading={isSubmitting}
                                >
                                    {isSubmitting
                                        ? (language === 'it' ? 'Invio in corso...' : 'Submitting...')
                                        : (language === 'it' ? 'Invia Richiesta' : 'Send Request')
                                    }
                                </Button>

                            </div>
                        </div>

                    </div>
                </div>

                <div id="images" className="hidden desktop:block w-2/5 fixed right-0 top-0 h-screen z-0">
                    <Slideshow />
                </div>
            </div>
        </>
    );
}
